interface EnsureScriptLoadedArgs {
  src: string;
  id: string;
}

const scriptPromises = new Map<string, Promise<void>>();

export const ensureScriptLoaded = ({ src, id }: EnsureScriptLoadedArgs): Promise<void> => {
  if (scriptPromises.get(id)) {
    return scriptPromises.get(id);
  }

  const scriptElementInDom = document.querySelector(`script#${id}`);
  if (scriptElementInDom) {
    return Promise.resolve();
  }

  const scriptElement = document.createElement('script');
  scriptElement.src = src;
  scriptElement.id = id;
  scriptElement.async = true;
  document.head.appendChild(scriptElement);

  const scriptLoadPromise = new Promise<void>((resolve, reject) => {
    scriptElement.addEventListener(
      'load',
      () => {
        resolve();
        scriptPromises.delete(id);
      },
      { once: true },
    );

    scriptElement.addEventListener(
      'error',
      () => {
        let responseStatus;
        try {
          const performanceResource = window.performance
            .getEntriesByType('resource')
            .filter((x): x is PerformanceResourceTiming => x instanceof PerformanceResourceTiming)
            .find((resource) => resource.initiatorType === 'script' && resource.name.includes(src));

          // @see https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/responseStatus
          if (typeof (performanceResource as unknown as { responseStatus?: number })?.responseStatus === 'number') {
            responseStatus = (performanceResource as unknown as { responseStatus?: number }).responseStatus;
          }
        } catch (e) {}

        const error = new Error(`Failed to load script ${id}. Response status: ${responseStatus}`);
        reject(error);
        scriptPromises.delete(id);
        scriptElement.parentElement.removeChild(scriptElement);
      },
      { once: true },
    );
  });
  scriptPromises.set(id, scriptLoadPromise);

  return scriptLoadPromise;
};
